import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { Loader2, ArrowLeft, AlertTriangle, FileText, Landmark, UserCircle, CalendarDays, Hash, HandCoins, Ban } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { getVoucher, listPaymentAllocations, cancelVoucher } from '@/app/services/voucherService';
import { VoucherResponse } from '@/app/types/voucher';
import { PaymentAllocationResponse, UnallocatedAmountResponse } from '@/app/types/payment';
import { getUnallocatedAmount } from '@/app/services/paymentService';
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from '@/components/ui/badge';
import { CancelPaymentModal } from '../components/CancelPaymentModal';
import { AllocateCreditModal } from '../components/AllocateCreditModal';
import { toast } from 'sonner';
import { getAccount } from '@/app/services/accountService';
import { AccountResponse } from '@/app/types/account';
import { RecordLinkIcon } from '@/app/components/RecordLinkIcon';

// Helper to format currency
const formatCurrency = (amount: number | undefined | null): string => {
  if (amount === undefined || amount === null) return 'N/A';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR', // TODO: Make dynamic
  }).format(Number(amount));
};

// Helper to format date
const formatDate = (dateString: string | Date | undefined | null): string => {
  if (!dateString) return 'N/A';
  try {
      const date = typeof dateString === 'string' ? parseISO(dateString) : dateString;
      // Adjust format as needed
      return format(date, 'dd MMM yyyy');
  } catch (error) {
    return String(dateString); // Fallback
  }
};

const PaymentsInDetailPage: React.FC = (): JSX.Element => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const paymentId = Number(id);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showAllocateModal, setShowAllocateModal] = useState(false);

  // Fetch Payment Voucher details
  const { data: paymentData, isLoading, isError, error } = useQuery<VoucherResponse, Error>({
    queryKey: ['voucher', paymentId],
    queryFn: () => getVoucher(paymentId),
    enabled: !!paymentId && !isNaN(paymentId),
  });

  // Fetch Payment Allocations
  const { data: allocationsData, isLoading: isLoadingAllocations } = useQuery<PaymentAllocationResponse[], Error>({
    queryKey: ['paymentAllocations', paymentId],
    queryFn: () => listPaymentAllocations(paymentId),
    enabled: !!paymentData, // Only run if payment data exists
  });

  // Fetch Unallocated Amount
  const { data: unallocatedData } = useQuery<UnallocatedAmountResponse, Error>({
    queryKey: ['unallocatedAmount', paymentId],
    queryFn: () => getUnallocatedAmount(paymentId),
    enabled: !!paymentData && !paymentData.is_cancelled, // Only run if payment data exists and not cancelled
  });

  // Fetch Account details if received_in_account_id is present
  const { data: accountData } = useQuery<AccountResponse, Error>({
    queryKey: ['account', (paymentData as any)?.received_in_account_id],
    queryFn: () => getAccount((paymentData as any).received_in_account_id),
    enabled: !!paymentData && !!(paymentData as any)?.received_in_account_id,
  });

  // Cancel Payment Mutation
  const cancelPaymentMutation = useMutation({
    mutationFn: () => cancelVoucher(paymentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['paymentsIn'] });
      queryClient.invalidateQueries({ queryKey: ['vouchers'] });
      queryClient.invalidateQueries({ queryKey: ['voucher', paymentId] });
      queryClient.invalidateQueries({ queryKey: ['paymentAllocations', paymentId] });
      toast.success('Payment cancelled successfully');
      setShowCancelModal(false);
      // Refetch the current payment data to update the UI
      queryClient.refetchQueries({ queryKey: ['voucher', paymentId] });
    },
    onError: (error: any) => {
      const errorMsg = error.response?.data?.detail || error.message || "Failed to cancel payment";
      toast.error(`Failed to cancel payment: ${errorMsg}`);
    },
  });

  // Display loading state
  if (isLoading) {
    return (
        <div className="container mx-auto py-6 space-y-6">
             <div className="flex items-center gap-4 mb-4">
                <Button variant="outline" size="icon" onClick={() => navigate(-1)}>
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <Skeleton className="h-8 w-48" /> {/* Skeleton for title */}
            </div>
            <Card>
                <CardHeader><Skeleton className="h-6 w-32" /></CardHeader>
                <CardContent className="space-y-4">
                    <Skeleton className="h-5 w-full" />
                    <Skeleton className="h-5 w-2/3" />
                    <Skeleton className="h-5 w-1/2" />
                </CardContent>
            </Card>
            {/* Skeleton for allocation table */}
            <Card>
                <CardHeader><Skeleton className="h-6 w-32" /></CardHeader>
                <CardContent>
                    <Skeleton className="h-32 w-full" />
                </CardContent>
            </Card>
        </div>
    );
  }

  // Display error state
  if (isError || !paymentData) {
    return (
      <div className="container mx-auto py-6 space-y-6">
         <div className="flex items-center gap-4 mb-4">
            <Button variant="outline" size="icon" onClick={() => navigate(-1)}>
                <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-2xl font-bold text-destructive">Error</h1>
         </div>
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Error loading payment details: {error instanceof Error ? error.message : 'Payment not found or could not be loaded.'}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
         <div className="flex items-center gap-4">
            <Button variant="outline" size="icon" onClick={() => navigate(-1)} aria-label="Go back">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-2xl font-bold">
              Payment Details: {paymentData.voucher_number}
            </h1>
             {paymentData.is_cancelled && <Badge variant="destructive">Cancelled</Badge>}
         </div>
         <div className="flex gap-2">
            {!paymentData.is_cancelled && unallocatedData && Number(unallocatedData.unallocated_amount) > 0 && (
              <Button 
                variant="default" 
                onClick={() => setShowAllocateModal(true)}
                className="gap-2"
              >
                <HandCoins className="h-4 w-4" />
                Allocate Credit
              </Button>
            )}
            {!paymentData.is_cancelled && (
              <Button 
                variant="destructive" 
                onClick={() => setShowCancelModal(true)}
                className="gap-2"
              >
                <Ban className="h-4 w-4" />
                Cancel Payment
              </Button>
            )}
         </div>
      </div>

      <Card>
          <CardHeader>
              <CardTitle>Summary</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-4 text-sm">
               {/* Row 1 */}
               <DetailItem icon={CalendarDays} label="Payment Date" value={formatDate(paymentData.transaction_date)} />
               <DetailItem icon={UserCircle} label="Customer" value={
                 <div className="flex items-center">
                   <span>{paymentData.party?.name || 'N/A'}</span>
                   {paymentData.party && (
                     <RecordLinkIcon 
                       recordType="party" 
                       recordId={paymentData.party.id} 
                       size="sm"
                     />
                   )}
                 </div>
               } />
               <DetailItem icon={Landmark} label="Received In Account" value={paymentData.received_in_account?.name || accountData?.name || 'N/A'} />

               {/* Row 2 */}
               <DetailItem icon={Hash} label="Payment #" value={paymentData.voucher_number} />
               <DetailItem icon={FileText} label="Reference #" value={paymentData.reference_number || '-'} />
               <DetailItem icon={HandCoins} label="Amount Received" value={formatCurrency(paymentData.total_amount)} className="font-semibold text-base" />

               {/* Row 3 (Narration) */}
               {paymentData.narration && (
                  <div className="md:col-span-3 mt-2">
                      <p className="text-muted-foreground font-medium">Notes:</p>
                      <p className="whitespace-pre-wrap">{paymentData.narration}</p>
                  </div>
               )}
          </CardContent>
      </Card>

      {/* Unallocated Amount Display */}
      {!paymentData.is_cancelled && unallocatedData && Number(unallocatedData.unallocated_amount) > 0 && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <HandCoins className="h-5 w-5 text-orange-600" />
                <div>
                  <p className="text-sm text-muted-foreground">Unallocated Amount</p>
                  <p className="text-2xl font-bold text-orange-600">
                    {formatCurrency(Number(unallocatedData.unallocated_amount))}
                  </p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                This amount can be allocated to outstanding invoices
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <Separator />

      {/* Allocations Table */}
      <Card>
          <CardHeader>
              <CardTitle>Invoice Allocations</CardTitle>
          </CardHeader>
          <CardContent>
              {isLoadingAllocations ? (
                <div className="flex justify-center items-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-primary mr-2" />
                  <span className="text-muted-foreground">Loading allocations...</span>
                </div>
              ) : allocationsData && allocationsData.length > 0 ? (
                <div className="rounded-md border">
                  <table className="min-w-full divide-y divide-border">
                    <thead>
                      <tr className="bg-muted/50">
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Invoice #</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Date</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">Allocated Amount</th>
                      </tr>
                    </thead>
                    <tbody className="bg-card divide-y divide-border">
                      {allocationsData.map((allocation) => (
                        <tr key={allocation.id}>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            <div className="flex items-center">
                              <span>{allocation.invoice_voucher?.voucher_number || `Invoice #${allocation.invoice_voucher_id}`}</span>
                              {allocation.invoice_voucher_id && (
                                <RecordLinkIcon 
                                  recordType="sales-invoice" 
                                  recordId={allocation.invoice_voucher_id} 
                                  size="sm"
                                />
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            {allocation.invoice_voucher?.transaction_date ? 
                              formatDate(allocation.invoice_voucher.transaction_date) : 
                              formatDate(allocation.allocation_date)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-medium">
                            {formatCurrency(allocation.allocated_amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-muted/50 border-t">
                        <th className="px-4 py-2 text-left text-sm" colSpan={2}>Total Allocated</th>
                        <th className="px-4 py-2 text-right text-sm">
                          {formatCurrency(allocationsData.reduce((sum, item) => sum + Number(item.allocated_amount), 0))}
                        </th>
                      </tr>
                      {unallocatedData && Number(unallocatedData.unallocated_amount) > 0 && (
                        <tr className="bg-muted/50">
                          <th className="px-4 py-2 text-left text-sm text-orange-600" colSpan={2}>Unallocated</th>
                          <th className="px-4 py-2 text-right text-sm text-orange-600">
                            {formatCurrency(Number(unallocatedData.unallocated_amount))}
                          </th>
                        </tr>
                      )}
                      <tr className="bg-muted/50 border-t">
                        <th className="px-4 py-3 text-left text-sm font-medium" colSpan={2}>Payment Total</th>
                        <th className="px-4 py-3 text-right text-sm font-medium">
                          {formatCurrency(paymentData.total_amount)}
                        </th>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <p className="text-muted-foreground py-4 text-center">No invoice allocations found for this payment.</p>
              )}
          </CardContent>
      </Card>

      {/* Cancel Payment Modal */}
      <CancelPaymentModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={() => cancelPaymentMutation.mutate()}
        receiptNumber={paymentData.voucher_number}
        isLoading={cancelPaymentMutation.isPending}
      />

      {/* Allocate Credit Modal */}
      {unallocatedData && paymentData.party && (
        <AllocateCreditModal
          isOpen={showAllocateModal}
          onClose={() => setShowAllocateModal(false)}
          paymentVoucherId={paymentId}
          voucherNumber={paymentData.voucher_number}
          availableCredit={Number(unallocatedData.unallocated_amount)}
          partyId={paymentData.party.id}
          partyName={paymentData.party.name}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['unallocatedAmount', paymentId] });
            queryClient.invalidateQueries({ queryKey: ['paymentAllocations', paymentId] });
            queryClient.invalidateQueries({ queryKey: ['voucher', paymentId] });
            setShowAllocateModal(false);
          }}
        />
      )}
    </div>
  );
};

// Helper component for consistent detail display
interface DetailItemProps {
    icon: React.ElementType;
    label: string;
    value: React.ReactNode;
    className?: string;
}

const DetailItem: React.FC<DetailItemProps> = ({ icon: Icon, label, value, className }) => (
    <div className={`flex items-start space-x-2 ${className}`}>
        <Icon className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" aria-hidden="true" />
        <div className="flex-1">
            <p className="text-muted-foreground">{label}</p>
            <p className="font-medium">{value}</p>
        </div>
    </div>
);

export default PaymentsInDetailPage; 